/**
 * Tipos y reglas del buzón de trámites del gremio (ADR #049 y #051).
 *
 * Las filas vienen de las RPC `bandeja_tramites` y `detalle_tramite`; las acciones, de
 * `gestionar_tramite`. Los filtros viajan en la URL para poder pasar un link a un compañero.
 */

export type TramiteEstado = 'recibido' | 'en_revision' | 'falta_informacion' | 'resuelto' | 'rechazado';

export const ESTADOS_ABIERTOS: TramiteEstado[] = ['recibido', 'en_revision', 'falta_informacion'];
export const ESTADOS_CERRADOS: TramiteEstado[] = ['resuelto', 'rechazado'];

const ESTADO_LABEL: Record<TramiteEstado, string> = {
  recibido: 'Recibido',
  en_revision: 'En revisión',
  falta_informacion: 'Falta información',
  resuelto: 'Resuelto',
  rechazado: 'Rechazado',
};

export function getEstadoTramiteLabel(estado: TramiteEstado | string): string {
  return ESTADO_LABEL[estado as TramiteEstado] ?? estado;
}

export function getEstadoTramiteColor(estado: TramiteEstado | string): 'default' | 'info' | 'warning' | 'success' | 'error' {
  switch (estado) {
    case 'en_revision':
      return 'info';
    case 'falta_informacion':
      return 'warning';
    case 'resuelto':
      return 'success';
    case 'rechazado':
      return 'error';
    default:
      return 'default';
  }
}

// ── Filtros ──────────────────────────────────────────────────────────────────

/** Selección de estado de la barra de chips (un solo valor). */
export type FiltroEstado = 'abiertos' | 'cerrados' | 'todos' | TramiteEstado;
export type FiltroAsignacion = 'todos' | 'mios' | 'sin_asignar';
export type FiltroEspera = '' | 'gremio' | 'afiliado';
export type FiltroOrden = 'antiguos' | 'recientes' | 'actividad';

export interface BandejaFiltros {
  estado: FiltroEstado;
  asignacion: FiltroAsignacion;
  tipoId: string;
  espera: FiltroEspera;
  /** Abiertos sin movimiento hace más de N días (0 = sin filtro). */
  inactivoDias: number;
  desde: string;
  hasta: string;
  empresaId: string;
  busqueda: string;
  orden: FiltroOrden;
  /** Página, desde 1. */
  pagina: number;
}

export const FILTROS_INICIALES: BandejaFiltros = {
  estado: 'abiertos',
  asignacion: 'todos',
  tipoId: '',
  espera: '',
  inactivoDias: 0,
  desde: '',
  hasta: '',
  empresaId: '',
  busqueda: '',
  orden: 'antiguos',
  pagina: 1,
};

export const TAMANO_PAGINA = 20;

const ESTADOS_VALIDOS: FiltroEstado[] = ['abiertos', 'cerrados', 'todos', 'recibido', 'en_revision', 'falta_informacion', 'resuelto', 'rechazado'];

export function estadosDelFiltro(estado: FiltroEstado): TramiteEstado[] | null {
  if (estado === 'todos') return null;
  if (estado === 'abiertos') return ESTADOS_ABIERTOS;
  if (estado === 'cerrados') return ESTADOS_CERRADOS;
  return [estado];
}

/** Filtros de la pantalla -> JSON que entiende `bandeja_tramites`. Omite lo que no filtra. */
export function filtrosToRpc(f: BandejaFiltros): Record<string, unknown> {
  const out: Record<string, unknown> = { orden: f.orden };
  const estados = estadosDelFiltro(f.estado);
  if (estados) out.estados = estados;
  if (f.asignacion !== 'todos') out.asignacion = f.asignacion;
  if (f.tipoId) out.tipo_id = f.tipoId;
  if (f.espera) out.espera = f.espera;
  if (f.inactivoDias > 0) out.inactivo_dias = f.inactivoDias;
  if (f.desde) out.desde = f.desde;
  if (f.hasta) out.hasta = f.hasta;
  if (f.empresaId) out.empresa_id = f.empresaId;
  if (f.busqueda.trim()) out.busqueda = f.busqueda.trim();
  return out;
}

/** URL -> filtros (valores desconocidos vuelven al default, nunca rompen la pantalla). */
export function filtrosFromParams(sp: URLSearchParams): BandejaFiltros {
  const estado = sp.get('estado') as FiltroEstado | null;
  const asignacion = sp.get('asig');
  const espera = sp.get('espera');
  const orden = sp.get('orden');
  const inact = Number(sp.get('sinmov') ?? 0);
  const pagina = Number(sp.get('p') ?? 1);
  const fecha = (v: string | null) => (v && /^\d{4}-(0[1-9]|1[0-2])-(0[1-9]|[12]\d|3[01])$/.test(v) ? v : '');
  return {
    estado: estado && ESTADOS_VALIDOS.includes(estado) ? estado : FILTROS_INICIALES.estado,
    asignacion: asignacion === 'mios' || asignacion === 'sin_asignar' ? asignacion : 'todos',
    tipoId: sp.get('tipo') ?? '',
    espera: espera === 'gremio' || espera === 'afiliado' ? espera : '',
    inactivoDias: Number.isInteger(inact) && inact > 0 && inact <= 365 ? inact : 0,
    desde: fecha(sp.get('desde')),
    hasta: fecha(sp.get('hasta')),
    empresaId: sp.get('empresa') ?? '',
    busqueda: sp.get('q') ?? '',
    orden: orden === 'recientes' || orden === 'actividad' ? orden : 'antiguos',
    pagina: Number.isInteger(pagina) && pagina >= 1 ? pagina : 1,
  };
}

/** Filtros -> URL (solo lo que difiere del default, para links cortos). */
export function filtrosToParams(f: BandejaFiltros): URLSearchParams {
  const sp = new URLSearchParams();
  if (f.estado !== FILTROS_INICIALES.estado) sp.set('estado', f.estado);
  if (f.asignacion !== 'todos') sp.set('asig', f.asignacion);
  if (f.tipoId) sp.set('tipo', f.tipoId);
  if (f.espera) sp.set('espera', f.espera);
  if (f.inactivoDias > 0) sp.set('sinmov', String(f.inactivoDias));
  if (f.desde) sp.set('desde', f.desde);
  if (f.hasta) sp.set('hasta', f.hasta);
  if (f.empresaId) sp.set('empresa', f.empresaId);
  if (f.busqueda.trim()) sp.set('q', f.busqueda.trim());
  if (f.orden !== 'antiguos') sp.set('orden', f.orden);
  if (f.pagina > 1) sp.set('p', String(f.pagina));
  return sp;
}

/** Cantidad de filtros activos además del estado y la búsqueda (los que van en el panel plegable). */
export function contarFiltrosAvanzados(f: BandejaFiltros): number {
  return [f.asignacion !== 'todos', !!f.tipoId, !!f.espera, f.inactivoDias > 0, !!f.desde, !!f.hasta, !!f.empresaId, f.orden !== 'antiguos'].filter(Boolean).length;
}

export type Conteos = Partial<Record<TramiteEstado, number>>;

/** Suma para los chips: abiertos, cerrados y todos. */
export function totalesDeChips(c: Conteos) {
  const suma = (l: TramiteEstado[]) => l.reduce((a, e) => a + (c[e] ?? 0), 0);
  return { abiertos: suma(ESTADOS_ABIERTOS), cerrados: suma(ESTADOS_CERRADOS), todos: suma([...ESTADOS_ABIERTOS, ...ESTADOS_CERRADOS]) };
}

// ── Filas y detalle ──────────────────────────────────────────────────────────

export interface FilaBandeja {
  id: string;
  numero: number;
  codigo: string;
  tipo_id: string;
  tipo: string;
  estado: TramiteEstado;
  resumen: string;
  afiliado_id: string;
  afiliado: string;
  cuil: string;
  asignado_a: string | null;
  asignado_nombre: string | null;
  adjuntos: number;
  created_at: string;
  updated_at: string;
  dias_sin_movimiento: number;
}

export interface BandejaResultado {
  total: number;
  conteos: Conteos;
  filas: FilaBandeja[];
  limite: number;
  desde: number;
}

export interface DetalleTramite {
  id: string;
  numero: number;
  codigo: string;
  tipo_id: string;
  tipo: string;
  requiere_adjunto: boolean;
  estado: TramiteEstado;
  detalle: string;
  asignado_a: string | null;
  asignado_nombre: string | null;
  created_at: string;
  updated_at: string;
  cerrado_at: string | null;
  afiliado: {
    id: string;
    apellido: string;
    nombre: string;
    cuil: string;
    telefono: string | null;
    email: string | null;
    estado: string;
    tiene_cuenta: boolean;
  };
  empresas: { razon_social: string; estado: string }[];
  eventos: {
    id: string;
    origen: 'afiliado' | 'gremio' | 'sistema';
    mensaje: string;
    estado_nuevo: TramiteEstado | null;
    created_at: string;
    autor: string | null;
  }[];
  adjuntos: { id: string; path: string; nombre: string; mime: string | null; tamano: number | null; evento_id: string | null; created_at: string }[];
}

export type AccionTramite = 'tomar' | 'pedir_informacion' | 'resolver' | 'rechazar';

export interface TipoTramiteAdmin {
  id: string;
  codigo: string;
  nombre: string;
  activo: boolean;
  orden: number;
}
